/**
 * @file SettingRows.tsx
 * @description Строки формы настроек склада (phase19.31, зона «Склад»;
 * разбор B — «Настройки склада»): переключатель и число с подписью.
 *
 * Переключатель — `Switch` вместо нативного чекбокса; вся строка — цель
 * касания (высота из темы), подпись и пояснение слева, переключатель справа
 * — рядом с подписью, а не в 900px от неё. Число — поле `Input` с цифрами
 * обычным шрифтом (моноширинный — только для кодов, SC43).
 */

import { useId } from "react";

import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";

/**
 * Карточка группы строк настроек (SC69, phase19.31): непрозрачная поверхность
 * темы с рамкой, строки разделены линией — подписи не лежат на фоне кабинета.
 */
export const SETTINGS_CARD_CLASS =
  "flex flex-col divide-y divide-[var(--border)] rounded-xl bg-surface-lowest px-4 py-1 [box-shadow:inset_0_0_0_1px_var(--border)]";

interface SwitchRowProps {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}

/** Строка «подпись + пояснение — переключатель». */
export function SettingSwitchRow({ label, hint, checked, disabled, onChange }: SwitchRowProps) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex min-h-[var(--control-h,3rem)] cursor-pointer items-center justify-between gap-4 py-2">
      <span className="flex min-w-0 flex-col">
        <span className="type-body font-medium text-on-surface">{label}</span>
        {hint ? <span className="type-caption text-on-surface-variant">{hint}</span> : null}
      </span>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={(next: boolean) => onChange(next)} aria-label={label} />
    </label>
  );
}

interface NumberRowProps {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

/** Строка «подпись — число». */
export function SettingNumberRow({ label, hint, value, min, max, onChange }: NumberRowProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1 py-2">
      <div className="flex min-h-[var(--control-h,3rem)] items-center justify-between gap-4">
        <label htmlFor={id} className="type-body min-w-0 font-medium text-on-surface">
          {label}
        </label>
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-24 shrink-0 text-right tabular-nums"
        />
      </div>
      {hint ? <p className="type-caption text-on-surface-variant">{hint}</p> : null}
    </div>
  );
}
